<template>
  <header class="screen-header">
    <div class="screen-header__top">
      <div class="screen-brand">
        <span class="screen-brand__mark"><i></i><i></i><i></i><i></i></span>
        <span :title="propertyName">{{ propertyName }}</span>
      </div>
      <div class="screen-title">
        <span class="screen-title__wing screen-title__wing--left"></span>
        <h1>物业系统运营数据大屏</h1>
        <span class="screen-title__wing screen-title__wing--right"></span>
      </div>
      <time class="screen-clock">{{ dateText }}&nbsp;&nbsp;{{ timeText }}&nbsp;&nbsp;{{ weekdayText }}</time>
    </div>
    <div class="screen-toolbar">
      <div class="screen-filter">
        <span>小区选择：</span>
        <button class="screen-select" type="button" :title="community" :aria-expanded="String(showCommunities)" aria-haspopup="listbox" @click="showCommunities = !showCommunities">
          {{ community }}<i class="el-icon-arrow-down"></i>
        </button>
        <div v-if="showCommunities" class="screen-select-menu" role="listbox">
          <button v-for="item in communityOptions" :key="item.id || 'all'" type="button" role="option" :aria-selected="String(isSelectedCommunity(item))" :class="{ active: isSelectedCommunity(item) }" @click="selectCommunity(item)">
            {{ item.name }}<i v-if="isSelectedCommunity(item)" class="el-icon-check"></i>
          </button>
        </div>
      </div>
      <div class="screen-period">
        <span>时间维度：</span>
        <div class="screen-period__options">
          <button v-for="item in periods" :key="item" type="button" :class="{ active: period === item }" @click="selectPeriod(item)">{{ item }}</button>
          <el-date-picker
            ref="customDatePicker"
            v-model="selectedRange"
            class="screen-date-trigger"
            :class="{ 'screen-date-trigger--selected': selectedRange.length === 2 }"
            type="daterange"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :clearable="false"
            :editable="false"
            :picker-options="pickerOptions"
            :title="dateRangeTitle"
            popper-class="dashboard-date-popper"
            unlink-panels
            @change="selectDateRange"
          />
        </div>
      </div>
      <button class="screen-refresh" type="button" :disabled="loading" @click="$emit('refresh')"><span></span>{{ freshnessText }}<i :class="loading ? 'el-icon-loading' : 'el-icon-refresh'"></i></button>
    </div>
  </header>
</template>

<script>
export default {
  name: 'DashboardHeader',
  props: {
    period: { type: String, default: '本月' },
    propertyName: { type: String, default: '当前物业' },
    community: { type: String, default: '全部小区' },
    communityId: { type: [String, Number], default: '' },
    communities: { type: Array, default: () => [] },
    dateRange: { type: Array, default: () => [] },
    lastUpdated: { type: String, default: '' },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      periods: ['本月', '本年', '自定义区间'],
      dateText: '',
      timeText: '',
      selectedRange: [],
      showCommunities: false,
      clockTimer: null,
      pickerOptions: {
        disabledDate(date) {
          return date.getTime() > Date.now()
        }
      }
    }
  },
  computed: {
    communityOptions() {
      return [{ id: '', name: '全部小区' }].concat(this.communities.map(item => ({ id: item.communityId, name: item.communityName })))
    },
    freshnessText() {
      if (this.loading) return '正在更新数据'
      if (!this.lastUpdated) return '等待数据更新'
      const date = new Date(this.lastUpdated)
      return `数据更新于 ${date.toLocaleTimeString('zh-CN', { hour12: false })}`
    },
    dateRangeTitle() {
      return this.selectedRange.length === 2 ? `${this.selectedRange[0]} 至 ${this.selectedRange[1]}` : '选择日期区间'
    },
    weekdayText() {
      const labels = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      return labels[new Date().getDay()]
    }
  },
  mounted() {
    this.selectedRange = this.dateRange.slice(0, 2)
    this.updateClock()
    this.clockTimer = window.setInterval(this.updateClock, 1000)
    document.addEventListener('mousedown', this.closeCommunityMenu)
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    if (this.clockTimer) window.clearInterval(this.clockTimer)
    document.removeEventListener('mousedown', this.closeCommunityMenu)
    document.removeEventListener('keydown', this.handleKeydown)
  },
  watch: {
    dateRange(value) {
      this.selectedRange = Array.isArray(value) ? value.slice(0, 2) : []
    }
  },
  methods: {
    selectPeriod(period) {
      if (period === '自定义区间') {
        this.openCustomRange()
        return
      }
      this.$emit('period-change', period)
    },
    openCustomRange() {
      this.$nextTick(() => {
        if (this.$refs.customDatePicker && this.$refs.customDatePicker.focus) {
          this.$refs.customDatePicker.focus()
        }
      })
    },
    selectDateRange(range) {
      if (Array.isArray(range) && range.length === 2) {
        this.$emit('date-range-change', range.slice())
      }
    },
    selectCommunity(item) {
      this.$emit('community-change', item.id)
      this.showCommunities = false
    },
    isSelectedCommunity(item) {
      return String(item.id || '') === String(this.communityId || '')
    },
    closeCommunityMenu(event) {
      if (this.showCommunities && this.$el && !this.$el.contains(event.target)) this.showCommunities = false
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.showCommunities = false
    },
    updateClock() {
      const now = new Date()
      this.dateText = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      this.timeText = now.toLocaleTimeString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style lang="scss" scoped>
.screen-header { height: 106px; }
.screen-header__top {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 54px;
  border-bottom: 1px solid rgba(0, 127, 201, .38);
}
.screen-brand, .screen-clock { z-index: 2; }
.screen-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: #d7e8f8;
  font-size: 16px;
}
.screen-brand > span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.screen-brand__mark {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  width: 23px;
  height: 26px;
}
.screen-brand__mark i { width: 4px; background: #28d7ee; box-shadow: 0 0 6px rgba(24, 215, 238, .45); transform: skewY(-18deg); }
.screen-brand__mark i:nth-child(1) { height: 15px; }
.screen-brand__mark i:nth-child(2) { height: 22px; }
.screen-brand__mark i:nth-child(3) { height: 17px; }
.screen-brand__mark i:nth-child(4) { height: 25px; }
.screen-title { position: relative; display: flex; align-items: center; justify-content: center; min-width: 610px; height: 54px; }
.screen-title h1 {
  z-index: 1;
  margin: 0;
  color: #eef7ff;
  font-size: 29px;
  font-weight: 700;
  letter-spacing: 0;
  text-shadow: 0 0 12px rgba(84, 165, 255, .35);
}
.screen-title::after {
  position: absolute;
  right: 35px;
  bottom: -7px;
  left: 35px;
  height: 9px;
  background: linear-gradient(90deg, transparent 0 11%, #078bde 11% 38%, transparent 38% 62%, #078bde 62% 89%, transparent 89%);
  clip-path: polygon(0 0, 36% 0, 39% 100%, 61% 100%, 64% 0, 100% 0, 100% 18%, 66% 18%, 62% 100%, 38% 100%, 34% 18%, 0 18%);
  content: '';
}
.screen-title__wing { position: absolute; bottom: 7px; width: 58px; height: 7px; background: repeating-linear-gradient(120deg, #087fe3 0 7px, transparent 7px 12px); }
.screen-title__wing--left { left: 35px; }
.screen-title__wing--right { right: 35px; transform: scaleX(-1); }
.screen-clock { justify-self: end; color: #e9f3fd; font-size: 17px; font-variant-numeric: tabular-nums; }
.screen-toolbar { display: flex; align-items: center; height: 48px; padding: 0 10px; color: #d4e3f0; font-size: 14px; }
.screen-filter, .screen-period { position: relative; display: flex; align-items: center; }
.screen-select, .screen-period__options {
  height: 34px;
  border: 1px solid rgba(42, 133, 194, .55);
  border-radius: 5px;
  background: rgba(13, 48, 75, .7);
  color: #ecf6ff;
}
.screen-select { display: flex; align-items: center; justify-content: space-between; width: 158px; padding: 0 14px; font-size: 14px; cursor: pointer; }
.screen-select-menu {
  position: absolute;
  z-index: 20;
  top: 37px;
  left: 70px;
  width: 158px;
  padding: 4px 0;
  max-height: 300px;
  overflow-x: hidden;
  overflow-y: auto;
  background: rgba(9, 41, 66, .98);
  border: 1px solid #166a9a;
  border-radius: 5px;
  box-shadow: 0 8px 20px rgba(0, 9, 19, .38);
}
.screen-select-menu button { display: flex; align-items: center; justify-content: space-between; width: 100%; height: 29px; padding: 0 14px; border: 0; background: transparent; color: #dae9f5; font-size: 14px; text-align: left; cursor: pointer; }
.screen-select-menu button.active, .screen-select-menu button:hover { color: #2699ff; background: rgba(29, 111, 170, .18); }
.screen-period { margin-left: 20px; }
.screen-period__options { display: flex; overflow: visible; }
.screen-period__options button { min-width: 72px; height: 32px; padding: 0 13px; border: 0; border-right: 1px solid rgba(68, 126, 166, .3); background: transparent; color: #cfdae6; font-size: 14px; cursor: pointer; }
.screen-period__options button.active { background: #277ce1; color: white; font-weight: 600; }
.screen-date-trigger { width: 42px !important; height: 32px; padding: 0 !important; border: 0; border-radius: 0 4px 4px 0; background: transparent; color: #cfdae6; cursor: pointer; }
.screen-date-trigger:hover { background: rgba(39, 124, 225, .18); }
.screen-date-trigger::v-deep .el-range-input,
.screen-date-trigger::v-deep .el-range-separator,
.screen-date-trigger::v-deep .el-range__close-icon { display: none; }
.screen-date-trigger::v-deep .el-range__icon { width: 42px; margin: 0; color: #cfdae6; font-size: 16px; line-height: 32px; }
.screen-date-trigger--selected { width: 218px !important; padding: 0 8px !important; }
.screen-date-trigger--selected::v-deep .el-range-input { display: inline-block; width: 78px; background: transparent; color: #dceaf5; font-size: 12px; }
.screen-date-trigger--selected::v-deep .el-range-separator { display: inline-block; width: 20px; color: #7f9aad; font-size: 11px; line-height: 30px; }
.screen-date-trigger--selected::v-deep .el-range__icon { width: 20px; margin-right: 3px; line-height: 30px; }
.screen-refresh { display: flex; align-items: center; gap: 9px; height: 32px; margin-left: auto; padding: 0 10px 0 12px; border: 1px solid rgba(54, 129, 177, .5); border-radius: 5px; background: transparent; color: #c7d6e3; font-size: 12px; cursor: pointer; }
.screen-refresh:disabled { opacity: .8; cursor: wait; }
.screen-refresh span { width: 8px; height: 8px; border-radius: 50%; background: #3dcc68; box-shadow: 0 0 8px #3dcc68; }
.screen-refresh i { padding-left: 9px; border-left: 1px solid rgba(99, 145, 174, .3); font-size: 16px; }
</style>

<style lang="scss">
.dashboard-date-popper.el-picker-panel {
  border-color: #176c9d;
  background: #06243a;
  color: #d8e7f2;
  box-shadow: 0 12px 30px rgba(0, 8, 18, .7);
}
.dashboard-date-popper .el-date-range-picker__content.is-left { border-right-color: rgba(59, 122, 163, .35); }
.dashboard-date-popper .el-date-range-picker__header,
.dashboard-date-popper .el-date-table th { color: #bfd1df; }
.dashboard-date-popper .el-date-table th { border-bottom-color: rgba(59, 122, 163, .3); }
.dashboard-date-popper .el-picker-panel__icon-btn { color: #9bb6c9; }
.dashboard-date-popper .el-date-table td.available:hover,
.dashboard-date-popper .el-date-table td.today span { color: #38b9ff; }
.dashboard-date-popper .el-date-table td.in-range div,
.dashboard-date-popper .el-date-table td.in-range div:hover { background: #0d4a70; }
.dashboard-date-popper .el-date-table td.start-date span,
.dashboard-date-popper .el-date-table td.end-date span { background: #277ce1; color: #fff; }
.dashboard-date-popper .el-date-table td.disabled div { background: #061b2c; color: #425b6d; }
.dashboard-date-popper[x-placement^='bottom'] .popper__arrow::after { border-bottom-color: #06243a; }
.dashboard-date-popper[x-placement^='top'] .popper__arrow::after { border-top-color: #06243a; }
</style>
